import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { apiClient } from '../api/client';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  Pagination,
  formatDate,
  formatEuro,
} from '../components';

interface MatchedFields {
  amount: number;
  date: number;
  description: number;
}

interface ReconciliationSuggestion {
  id: string;
  transactionId: string;
  receiptId: string;
  confidenceScore: number;
  matchedFields: MatchedFields;
  status: 'pending' | 'accepted' | 'rejected' | 'auto_accepted';
  createdAt: string;
  transaction?: {
    date: string;
    amount: number;
    description: string | null;
    counterparty: string | null;
  };
  receipt?: {
    filename: string;
    createdAt: string;
  };
}

interface ReconciliationSummary {
  totalSuggestions: number;
  pending: number;
  accepted: number;
  rejected: number;
  autoAccepted: number;
  averageConfidence: number;
}

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-red-100 text-red-800',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  auto_accepted: 'bg-blue-100 text-blue-800',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Odottaa',
  accepted: 'Hyväksytty',
  rejected: 'Hylätty',
  auto_accepted: 'Automaattinen',
};

function getConfidenceColor(score: number): string {
  if (score >= 95) return CONFIDENCE_COLORS.high;
  if (score >= 70) return CONFIDENCE_COLORS.medium;
  return CONFIDENCE_COLORS.low;
}

export default function AIReconciliationPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [suggestions, setSuggestions] = useState<ReconciliationSuggestion[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: 'pending' as 'pending' | 'accepted' | 'rejected' | 'auto_accepted' | '',
    minConfidence: '',
  });
  const limit = 25;

  const fetchSuggestions = useCallback(async () => {
    if (!activeOrg) return;

    setLoading(true);
    setError('');

    try {
      const offset = (currentPage - 1) * limit;
      const params = new URLSearchParams();
      params.append('organization_id', activeOrg.id);
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      if (filters.status) params.append('status', filters.status);
      if (filters.minConfidence) params.append('min_confidence', filters.minConfidence);

      const data = await apiClient.get<{
        suggestions: ReconciliationSuggestion[];
        pagination: { total: number };
      }>(`/reconciliation/suggestions?${params.toString()}`);

      setSuggestions(data.suggestions);
      setTotalCount(data.pagination.total);
    } catch (err) {
      setError('Ehdotusten haku epäonnistui');
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, currentPage, filters]);

  const fetchSummary = useCallback(async () => {
    if (!activeOrg) return;

    try {
      const data = await apiClient.get<ReconciliationSummary>(`/reconciliation/summary?organization_id=${activeOrg.id}`);
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [activeOrg]);

  const generateSuggestions = async () => {
    if (!activeOrg) return;

    setGenerating(true);
    setError('');

    try {
      const data = await apiClient.post<{
        suggestions_created: number;
        auto_accepted: number;
      }>('/reconciliation/ai-match', {
        organization_id: activeOrg.id,
        auto_accept_threshold: 95,
      });

      alert(`Luotu ${data.suggestions_created} ehdotusta, joista ${data.auto_accepted} hyväksytty automaattisesti.`);
      fetchSuggestions();
      fetchSummary();
    } catch (err) {
      setError('Ehdotusten generointi epäonnistui');
      console.error('Failed to generate suggestions:', err);
    } finally {
      setGenerating(false);
    }
  };

  const acceptSuggestion = async (suggestionId: string) => {
    if (!activeOrg) return;

    setProcessing(suggestionId);
    setError('');

    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/accept?organization_id=${activeOrg.id}`, {});
      fetchSuggestions();
      fetchSummary();
    } catch (err) {
      setError('Hyväksyminen epäonnistui');
      console.error('Failed to accept suggestion:', err);
    } finally {
      setProcessing(null);
    }
  };

  const rejectSuggestion = async (suggestionId: string) => {
    if (!activeOrg) return;

    setProcessing(suggestionId);
    setError('');

    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/reject?organization_id=${activeOrg.id}`, {});
      fetchSuggestions();
      fetchSummary();
    } catch (err) {
      setError('Hylkääminen epäonnistui');
      console.error('Failed to reject suggestion:', err);
    } finally {
      setProcessing(null);
    }
  };

  useEffect(() => {
    fetchSuggestions();
    fetchSummary();
  }, [fetchSuggestions, fetchSummary]);

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="AI-Täsmäytys" />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card title="Kaikki ehdotukset">
            <div className="text-3xl font-bold text-indigo-600">{summary.totalSuggestions}</div>
          </Card>
          <Card title="Odottaa" className="border-l-4 border-yellow-500">
            <div className="text-3xl font-bold text-yellow-600">{summary.pending}</div>
          </Card>
          <Card title="Hyväksytty" className="border-l-4 border-green-500">
            <div className="text-3xl font-bold text-green-600">{summary.accepted + summary.autoAccepted}</div>
            <div className="text-xs text-gray-500">({summary.autoAccepted} auto)</div>
          </Card>
          <Card title="Keskim. luottamus">
            <div className="text-3xl font-bold text-indigo-600">{summary.averageConfidence}%</div>
          </Card>
        </div>
      )}

      {/* Actions */}
      <Card title="Toiminnot">
        <div className="flex gap-4">
          <Button variant="primary" onClick={generateSuggestions} disabled={generating}>
            {generating ? '🤔 Analysoidaan...' : '🤔 Generoi AI-ehdotukset'}
          </Button>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          AI analysoi tapahtumat ja tositteet, ehdottaa täsmäytyksiä luottamusarvion kera.
          Ehdotukset {'>'} 95% hyväksytään automaattisesti.
        </p>
      </Card>

      {/* Filters */}
      <Card title="Suodattimet">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Tila"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value as any }))}
            options={[
              { value: '', label: 'Kaikki' },
              { value: 'pending', label: 'Odottaa' },
              { value: 'accepted', label: 'Hyväksytty' },
              { value: 'rejected', label: 'Hylätty' },
              { value: 'auto_accepted', label: 'Automaattinen' },
            ]}
          />
          <Select
            label="Minimi luottamus"
            value={filters.minConfidence}
            onChange={(e) => setFilters(f => ({ ...f, minConfidence: e.target.value }))}
            options={[
              { value: '', label: 'Kaikki' },
              { value: '95', label: '95% (Korkea)' },
              { value: '70', label: '70% (Keskitaso)' },
              { value: '50', label: '50% (Matala)' },
            ]}
          />
          <div className="flex items-end">
            <Button variant="secondary" onClick={fetchSuggestions}>
              🔄 Päivitä
            </Button>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Suggestions Table */}
      <Card title={`AI-Ehdotukset (${totalCount})`}>
        <DataTable>
          <Table
            columns={[
              {
                key: 'confidence',
                header: t('reconciliation.confidence'),
                width: '120px',
                render: (s: ReconciliationSuggestion) => (
                  <Badge className={getConfidenceColor(s.confidenceScore)}>
                    {s.confidenceScore}%
                  </Badge>
                ),
              },
              {
                key: 'status',
                header: 'Tila',
                width: '120px',
                render: (s: ReconciliationSuggestion) => (
                  <Badge className={STATUS_COLORS[s.status]}>
                    {STATUS_LABELS[s.status]}
                  </Badge>
                ),
              },
              {
                key: 'transaction',
                header: 'Tapahtuma',
                render: (s: ReconciliationSuggestion) => (
                  <div>
                    <div className="font-medium">
                      {s.transaction?.counterparty || s.transaction?.description || 'Tuntematon'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(s.transaction?.date || '')} • {formatEuro(s.transaction?.amount || 0)}
                    </div>
                  </div>
                ),
              },
              {
                key: 'receipt',
                header: 'Tosite',
                render: (s: ReconciliationSuggestion) => (
                  <div className="text-sm">
                    {s.receipt?.filename || 'Tuntematon'}
                  </div>
                ),
              },
              {
                key: 'matches',
                header: 'Täsmäytys',
                width: '150px',
                render: (s: ReconciliationSuggestion) => (
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span>Summa:</span>
                      <span className={s.matchedFields.amount >= 0.9 ? 'text-green-600' : 'text-gray-500'}>
                        {Math.round(s.matchedFields.amount * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Päivä:</span>
                      <span className={s.matchedFields.date >= 0.9 ? 'text-green-600' : 'text-gray-500'}>
                        {Math.round(s.matchedFields.date * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Kuvaus:</span>
                      <span className={s.matchedFields.description >= 0.9 ? 'text-green-600' : 'text-gray-500'}>
                        {Math.round(s.matchedFields.description * 100)}%
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                key: 'actions',
                header: '',
                width: '150px',
                align: 'right',
                render: (s: ReconciliationSuggestion) => (
                  s.status === 'pending' ? (
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => acceptSuggestion(s.id)}
                        disabled={processing === s.id}
                      >
                        ✓
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => rejectSuggestion(s.id)}
                        disabled={processing === s.id}
                      >
                        ✕
                      </Button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Käsitelty</span>
                  )
                ),
              },
            ]}
            data={suggestions}
            keyExtractor={(s) => s.id}
            loading={loading}
            emptyMessage="Ei AI-ehdotuksia valituilla suodattimilla"
          />
        </DataTable>

        {totalCount > limit && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / limit)}
              onPageChange={setCurrentPage}
              totalItems={totalCount}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
